/**
 * Public-facing multi-step navigation for campaigns and tools.
 * RTL and mobile accessible. Shows progress bar, page names, and back/next controls.
 */
import React from 'react';
import type { MultiPageDef } from '../../types';
import { sortedPages } from './multiPageUtils';

interface MultiStepNavigationProps {
  pages: MultiPageDef[];
  currentPageId: string;
  onNavigate: (pageId: string) => void;
  /** If true, the next button is disabled (e.g. validation failed) */
  canAdvance: boolean;
  /** Label for the final-page button (e.g. "תרום" or "שלח") */
  finalLabel?: string;
  /** Called when user clicks next on the final page */
  onFinalSubmit?: () => void;
  primaryColor?: string;
}

const MultiStepNavigation: React.FC<MultiStepNavigationProps> = ({
  pages,
  currentPageId,
  onNavigate,
  canAdvance,
  finalLabel = 'שלח',
  onFinalSubmit,
  primaryColor = '#2563eb',
}) => {
  const sorted = sortedPages(pages);
  const currentIdx = sorted.findIndex(p => p.id === currentPageId);
  const isFirst = currentIdx === 0;
  const isLast = currentIdx === sorted.length - 1;

  const goNext = () => {
    if (isLast) {
      onFinalSubmit?.();
    } else if (canAdvance) {
      onNavigate(sorted[currentIdx + 1].id);
    }
  };

  const goBack = () => {
    if (!isFirst) onNavigate(sorted[currentIdx - 1].id);
  };

  return (
    <div dir="rtl" className="w-full">
      {/* Progress bar */}
      <div className="flex items-center justify-center gap-1 mb-4 px-4" role="progressbar" aria-valuenow={currentIdx + 1} aria-valuemin={1} aria-valuemax={sorted.length}>
        {sorted.map((page, idx) => {
          const isActive = idx === currentIdx;
          const isDone = idx < currentIdx;
          return (
            <React.Fragment key={page.id}>
              {idx > 0 && (
                <div
                  className="flex-1 h-0.5 max-w-[60px] rounded-full transition-colors duration-300"
                  style={{ backgroundColor: isDone ? primaryColor : '#e5e7eb' }}
                />
              )}
              <button
                onClick={() => {
                  if (idx < currentIdx) onNavigate(page.id);
                }}
                disabled={idx > currentIdx}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
                  isActive
                    ? 'text-white shadow-md'
                    : isDone
                    ? 'text-white opacity-70 hover:opacity-100 cursor-pointer'
                    : 'text-gray-400 bg-gray-100 cursor-default'
                }`}
                style={isActive || isDone ? { backgroundColor: primaryColor } : {}}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`${page.name} - שלב ${idx + 1} מתוך ${sorted.length}`}
              >
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] border-2 border-white/30">
                  {isDone ? '✓' : idx + 1}
                </span>
                <span className="hidden sm:inline">{page.name}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Back / Next buttons */}
      <div className="flex items-center justify-between gap-3 px-4">
        <button
          onClick={goBack}
          disabled={isFirst}
          className={`flex items-center gap-1 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
            isFirst
              ? 'opacity-0 pointer-events-none'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95'
          }`}
          aria-label="חזור לעמוד הקודם"
        >
          <svg className="w-4 h-4 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          הקודם
        </button>

        <button
          onClick={goNext}
          disabled={!canAdvance && !isLast}
          className="flex items-center gap-1 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          style={{ backgroundColor: primaryColor }}
          aria-label={isLast ? finalLabel : 'המשך לעמוד הבא'}
        >
          {isLast ? finalLabel : 'הבא'}
          {!isLast && (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default MultiStepNavigation;
